import NavLink from 'next/link';
import {useTranslation} from 'next-i18next';
import PropTypes from 'prop-types';
import React, {useCallback, useContext} from 'react';

import {LANGUAGES} from '@/src/app/constants/course';
import CourseVideoPreviewPop from '@/src/courseDetails/components/CourseVideoPreviewPop';
import useIsMobile from '@/src/hooks/useIsMobile';
import useRoutePaths from '@/src/hooks/useRoutePaths';
import PopupContext from '@/src/popup/utils/PopupContext';
import TextLocal from '@/src/textLocal/containers/TextLocal';

import {Button, Separator, Text} from '../../../ui';
import css from '../../styles/CoursePreview.module.css';

const CoursePreview = ({
  video,
  preview,
  title,
  duration,
  languages,
  difficultyLevel,
  topicsCount,
  availableTranslations,
  isBookmarked,
  onCourseBookMarkToggleClick,
  isBookmarkedLoading,
  onWatchCourseClick,
}) => {
  const {t} = useTranslation('common');
  const {COURSES_ROUTE} = useRoutePaths();
  const {openPopup} = useContext(PopupContext);

  const {isMaxWidth600} = useIsMobile();
  const getLanguage = useCallback((translation) => {
    const language = LANGUAGES.find((language) => {
      return language.value === translation.value;
    });
    return t(language.label);
  }, []);

  const handleOpenCoursePreview = useCallback(() => {
    // @ts-ignore
    openPopup({
      popup: (
        <CourseVideoPreviewPop
          videUrl={video}
          onWatchCourseClick={onWatchCourseClick}
        />
      ),
      options: {withCloseButton: false, withTransparentBackground: true},
    });
  }, [video, title]);

  return (
    <div className={css.coursePreview}>
      <div className={css.coursePreviewImage} onClick={handleOpenCoursePreview}>
        <img src={preview} loading="lazy" alt="" />
        <div className={css.watchFullContent}>
          <TextLocal id={'courseDetails.watch-preview'} />
        </div>
      </div>

      <div className={css.coursePreviewActions}>
        <Button height={Button.HEIGHT.REGULAR} onClick={onWatchCourseClick}>
          <Text
            tagComponent={Text.TAG.SPAN}
            fontSize={
              isMaxWidth600 ? Text.FONT_SIZE.SIZE_12 : Text.FONT_SIZE.SIZE_16
            }
            fontWeight={Text.FONT_WEIGHT.BOLD}
          >
            <TextLocal id={'courseDetails.watch-now'} />
          </Text>
        </Button>
        <Button
          height={Button.HEIGHT.REGULAR}
          color={Button.COLOR.SECONDARY}
          border={Button.BORDER.BOLD}
          onClick={onCourseBookMarkToggleClick}
          isLoading={isBookmarkedLoading}
        >
          <Text
            tagComponent={Text.TAG.SPAN}
            fontSize={
              isMaxWidth600 ? Text.FONT_SIZE.SIZE_12 : Text.FONT_SIZE.SIZE_16
            }
            fontWeight={Text.FONT_WEIGHT.BOLD}
            lineHeight={Text.LINE_HEIGHT.LINE_HEIGHT_1_6}
          >
            <TextLocal
              id={
                isBookmarked
                  ? 'courseDetails.remove-from-bookmark'
                  : 'courseDetails.add-to-bookmark'
              }
            />
          </Text>
        </Button>

        {availableTranslations.map((translation) => (
          <NavLink
            href={COURSES_ROUTE.getLink(translation.slug)}
            key={translation.value}
          >
            <a
              className={css.courseAnotherLanguage}
              aria-label={`chagne lang to ${getLanguage(translation)}`}
            >
              <Text
                lineHeight={Text.LINE_HEIGHT.LINE_HEIGHT_1_6}
                color={Text.COLOR.GRADIENT}
                fontWeight={Text.FONT_WEIGHT.MEDIUM}
                fontSize={Text.FONT_SIZE.SIZE_16}
              >
                <TextLocal
                  id={'courseDetails.course-in-another-language'}
                  params={{
                    language: getLanguage(translation),
                  }}
                />
              </Text>
            </a>
          </NavLink>
        ))}

        <Separator borderWeight={Separator.BORDER_WEIGHT.THIN} />
      </div>

      <div className={css.coursePreviewInfo}>
        <div className={css.coursePreviewInfoRow}>
          <Text color={Text.COLOR.GREY} fontSize={Text.FONT_SIZE.SIZE_16}>
            <TextLocal id={'courseDetails.difficulty-level'} />
          </Text>
          <Text
            fontWeight={Text.FONT_WEIGHT.BOLD}
            fontSize={Text.FONT_SIZE.SIZE_16}
          >
            <TextLocal id={difficultyLevel.label} />
          </Text>
        </div>

        <div className={css.coursePreviewInfoRow}>
          <Text color={Text.COLOR.GREY} fontSize={Text.FONT_SIZE.SIZE_16}>
            <TextLocal id={'courseDetails.amount-of-topics'} />
          </Text>
          <Text
            fontWeight={Text.FONT_WEIGHT.BOLD}
            fontSize={Text.FONT_SIZE.SIZE_16}
          >
            {topicsCount}
          </Text>
        </div>

        <div className={css.coursePreviewInfoRow}>
          <Text color={Text.COLOR.GREY} fontSize={Text.FONT_SIZE.SIZE_16}>
            <TextLocal id={'courseDetails.certificate-of-completion'} />
          </Text>
          <Text
            fontWeight={Text.FONT_WEIGHT.BOLD}
            fontSize={Text.FONT_SIZE.SIZE_16}
          >
            <TextLocal id={'general.yes'} />
          </Text>
        </div>

        <div className={css.coursePreviewInfoRow}>
          <Text color={Text.COLOR.GREY} fontSize={Text.FONT_SIZE.SIZE_16}>
            <TextLocal id={'courseDetails.language'} />
          </Text>
          <Text
            fontWeight={Text.FONT_WEIGHT.BOLD}
            fontSize={Text.FONT_SIZE.SIZE_16}
          >
            {languages.map((language, index) => (
              <span key={language.value}>
                <TextLocal id={language.label} />
                {languages.length - 1 !== index && ', '}
              </span>
            ))}
          </Text>
        </div>

        <div className={css.coursePreviewInfoRow}>
          <Text color={Text.COLOR.GREY} fontSize={Text.FONT_SIZE.SIZE_16}>
            <TextLocal id={'courseDetails.duration'} />
          </Text>
          <Text
            fontWeight={Text.FONT_WEIGHT.BOLD}
            fontSize={Text.FONT_SIZE.SIZE_16}
          >
            {duration}
          </Text>
        </div>
      </div>
    </div>
  );
};

CoursePreview.propTypes = {
  video: PropTypes.string.isRequired,
  preview: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  duration: PropTypes.string.isRequired,
  languages: PropTypes.array.isRequired,
  hasCertificate: PropTypes.string,
  topicsCount: PropTypes.string.isRequired,
  difficultyLevel: PropTypes.object.isRequired,
  availableTranslations: PropTypes.array.isRequired,
  isBookmarked: PropTypes.bool.isRequired,
  isBookmarkedLoading: PropTypes.bool.isRequired,
  onCourseBookMarkToggleClick: PropTypes.func.isRequired,
  onWatchCourseClick: PropTypes.func.isRequired,
};

export default CoursePreview;
